import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
} from '@mui/material';
import { debounce } from '@material-ui/core';

import { IoIosAdd } from 'react-icons/io';
import { HiPencil } from 'react-icons/hi';
import { MdDelete } from 'react-icons/md';
import { useQueryClient, useMutation } from 'react-query';
import classNames from 'classnames';

import styles from './styles.module.scss';
import stylesTable from 'styles/table.module.scss';
import CreateOrUpdateMailTemplate from './CreateOrUpdateMailTemplate.js';
import { apiDeleteMailTemplate } from 'api/mailTemplate';
import { useGetListMailTemplate } from 'hook/useMailTemplate';

import { CANDIDATE_PERMISSION } from 'constants/permission';
import { DEFAULT_PAGE_MUI, DEFAULT_LIMIT_20, PER_PAGE_OPTION_20, MAX_LENGTH_50, LOADING_SM } from 'constants/index.js';
import { ZERO } from 'constants/MasterData';
import { USE_GET_LIST_MAIL_TEMPLATE, MAIL_ACTIVE } from 'constants/MailTemplate';
import { HTTP_OK } from 'constants/StatusCode.js';

import PopupAreYouSure from 'components/PopupAreYouSure';
import AlertComponent from 'components/AlertAfterSubmit/index.js';
import SearchComponent from 'components/Search';
import ButtonComponent from 'components/Buttons';
import EditLayout from 'components/PermissionPage/EditLayout';
import DeleteLayout from 'components/PermissionPage/DeleteLayout';
import Loading from 'components/Loading';

import { getSTT, displayLabelTablePagination } from 'helpers/table';
import { showAlert } from 'helpers/modal';
import { getMailTriggerText, getSendModeText } from 'helpers/mailTemplate';

const DEFAULT_FILTER = {
  keyword: null,
  page: DEFAULT_PAGE_MUI,
  per_page: DEFAULT_LIMIT_20,
};

function ManagerMailTemplate() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [idMailTemplate, setIdMailTemplate] = useState();
  const [isShowModal, setShowModal] = useState(false);
  const [formAdd, setFormAdd] = useState(null);
  const [message, setMessage] = useState('');
  const [isShowAlert, setShowAlert] = useState(false);
  const [filter, setFilter] = useState(DEFAULT_FILTER);

  const { data: dataListMailTemplate, isLoading: isLoadingList } = useGetListMailTemplate({
    ...filter,
    page: filter.page + 1,
  });

  const handleChangeRowsPerPage = (event) => {
    setFilter({ ...filter, page: DEFAULT_PAGE_MUI, per_page: +event.target.value });
    window.scrollTo({ top: ZERO, behavior: 'smooth' });
  };

  const handleChangePage = (event, newPage) => {
    setFilter({ ...filter, page: newPage });
    window.scrollTo({ top: ZERO, behavior: 'smooth' });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce((value) => {
      setFilter({
        ...filter,
        keyword: value,
        page: DEFAULT_PAGE_MUI,
        per_page: DEFAULT_LIMIT_20,
      });
    }),
  );

  const openAddModal = () => {
    setFormAdd(null);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setFormAdd(item);
    setShowModal(true);
  };

  const openPopupConfirmDelete = (item) => {
    setConfirmDelete(true);
    setIdMailTemplate(item.id);
  };

  const onSaveSuccess = (msg) => {
    setMessage(msg);
    setShowAlert(true);
  };

  const { mutate: deleteMailTemplate, isLoading: isLoadingDelete } = useMutation(
    () => apiDeleteMailTemplate({ id: idMailTemplate }),
    {
      onSuccess: (response) => {
        setConfirmDelete(false);
        if (response?.data?.status === HTTP_OK) {
          queryClient.invalidateQueries([USE_GET_LIST_MAIL_TEMPLATE], { refetchActive: true });
          setMessage(t('managerMailTemplate.deleteSuccess'));
        } else {
          setMessage(response?.data?.message || t('messages.systemError'));
        }
        setShowAlert(true);
      },
      onError: () => {
        showAlert(t('messages.systemError'));
      },
    },
  );

  const FooterPopupConfirm = ({ onClickCancel, onClickOk }) => {
    if (isLoadingDelete)
      return (
        <div className={styles.loadingIcon}>
          <Loading type={LOADING_SM} />
        </div>
      );
    return (
      <>
        <ButtonComponent
          addClass={classNames(styles.btnConfirm, styles.btnConfirm__cancel)}
          text={t('common.cancel')}
          handleClick={onClickCancel}
        />
        <ButtonComponent
          addClass={classNames(styles.btnConfirm, styles.btnConfirm__ok)}
          text={t('common.ok')}
          handleClick={onClickOk}
        />
      </>
    );
  };

  return (
    <>
      <PopupAreYouSure
        active={confirmDelete}
        handleCancel={() => setConfirmDelete(false)}
        messages={t('managerMailTemplate.confirmDelete')}
        footer={
          <FooterPopupConfirm onClickCancel={() => setConfirmDelete(false)} onClickOk={() => deleteMailTemplate()} />
        }
      />
      <Grid container mb={2}>
        <Grid item xs={8} className={styles.headerTitle}>
          {t('managerMailTemplate.title')}
        </Grid>
        <Grid container item xs={4} align="end" columnSpacing={2}>
          <Grid item xs={8}>
            <SearchComponent
              addClass={styles.inputSearchBox}
              txtPlaceholder={t('managerMailTemplate.searchPlaceholder')}
              defaultValue={filter?.keyword}
              handleSearch={handleSearch}
              maxLength={MAX_LENGTH_50}
              size={'small'}
            />
          </Grid>
          <Grid item xs={4}>
            <EditLayout permissionName={CANDIDATE_PERMISSION}>
              <ButtonComponent
                addClass={styles.buttonAdd}
                handleClick={() => openAddModal()}
                text={t('common.add')}
                icon={<IoIosAdd />}
              />
            </EditLayout>
          </Grid>
        </Grid>
      </Grid>
      <Grid className={styles.listContainer}>
        <div className="table-border-show">
          <Paper>
            <TableContainer className={stylesTable.tableCommon}>
              <Table aria-label="sticky table">
                <TableHead className="asset-header">
                  <TableRow className="asset-header-row headTable">
                    <TableCell className={styles.colorHeader}>{t('common.STT')}</TableCell>
                    <TableCell className={styles.colorHeader}>{t('managerMailTemplate.name')}</TableCell>
                    <TableCell className={styles.colorHeader}>{t('managerMailTemplate.subject')}</TableCell>
                    <TableCell className={styles.colorHeader}>{t('managerMailTemplate.trigger')}</TableCell>
                    <TableCell className={styles.colorHeader}>{t('managerMailTemplate.sendMode')}</TableCell>
                    <TableCell className={styles.colorHeader}>{t('managerMailTemplate.status')}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingList && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Loading addClass="mt-3 mb-3" />
                      </TableCell>
                    </TableRow>
                  )}

                  {!isLoadingList &&
                    dataListMailTemplate?.data?.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell className={classNames(stylesTable.sttCell, styles.textSTT, styles.colIndex)}>
                          {getSTT(index + filter.page * filter.per_page)}
                        </TableCell>
                        <TableCell className={styles.colorText}>{item?.name}</TableCell>
                        <TableCell className={styles.colorText}>{item?.subject}</TableCell>
                        <TableCell className={styles.colorText}>{getMailTriggerText(t, item)}</TableCell>
                        <TableCell className={styles.colorText}>{getSendModeText(t, item)}</TableCell>
                        <TableCell>
                          <span
                            className={classNames(
                              styles.statusTag,
                              item?.is_active === MAIL_ACTIVE ? styles.statusTag__active : styles.statusTag__inactive,
                            )}
                          >
                            {item?.is_active === MAIL_ACTIVE
                              ? t('managerMailTemplate.active')
                              : t('managerMailTemplate.inactive')}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className={styles.flexIcon}>
                            <EditLayout permissionName={CANDIDATE_PERMISSION}>
                              <div className="edit-button mr-15" onClick={() => openEditModal(item)}>
                                <HiPencil />
                              </div>
                            </EditLayout>
                            <DeleteLayout permissionName={CANDIDATE_PERMISSION}>
                              <div className="edit-button" onClick={() => openPopupConfirmDelete(item)}>
                                <MdDelete />
                              </div>
                            </DeleteLayout>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}

                  {!isLoadingList && !dataListMailTemplate?.total && (
                    <tr>
                      <td colSpan={7} className="error-no-data">
                        {t('common.notData')}
                      </td>
                    </tr>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {dataListMailTemplate?.total > 0 && (
              <TablePagination
                rowsPerPageOptions={PER_PAGE_OPTION_20}
                component="div"
                count={dataListMailTemplate?.total}
                rowsPerPage={filter?.per_page}
                page={filter.page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                className="table-pagination"
                labelDisplayedRows={displayLabelTablePagination}
              />
            )}
          </Paper>
        </div>
      </Grid>
      {isShowModal && (
        <CreateOrUpdateMailTemplate
          isShowModal={isShowModal}
          setShowModal={setShowModal}
          formAdd={formAdd}
          onSuccess={onSaveSuccess}
        />
      )}
      <AlertComponent toggle={() => setShowAlert(false)} isShowAlert={isShowAlert}>
        <p>{message}</p>
      </AlertComponent>
    </>
  );
}

export default ManagerMailTemplate;
